
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Person} from '../entity/Person';
import {FieldModelPerson} from '../entity/Person';

/**
 * PersonUpdateResult struct
 */
class PersonUpdateResult {
  /**
   * Initialize struct
   * @param {!Person=} person
   * @constructor
   */
  constructor (person = new Person()) {
    this.Person = person
  }

  /**
   * Copy struct (shallow copy)
   * @this {!PersonUpdateResult}
   * @param {!PersonUpdateResult} other Other struct
   * @returns {!PersonUpdateResult} This struct
   */
  copy (other) {
    if (other.Person != null) {
      this.Person = Person.fromObject(other.Person)
    } else {
      this.Person = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!PersonUpdateResult}
   * @returns {!PersonUpdateResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new PersonUpdateResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new PersonUpdateResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!PersonUpdateResult}
   * @param {!PersonUpdateResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof PersonUpdateResult)) {
      throw new TypeError('Instance of PersonUpdateResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!PersonUpdateResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Person: ((this.Person != null) ? this.Person : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return PersonUpdateResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!PersonUpdateResult} other Object value
   * @returns {!PersonUpdateResult} Created struct
   */
  static fromObject (other) {
    return new PersonUpdateResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!PersonUpdateResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return PersonUpdateResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!PersonUpdateResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10032
  }
}

export { PersonUpdateResult };

/**
 * Fast Binary Encoding PersonUpdateResult field model
 */
class FieldModelPersonUpdateResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Person = new FieldModelPerson(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Person.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Person.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelPersonUpdateResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10032
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelPersonUpdateResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelPersonUpdateResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Person.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Person.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Person.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelPersonUpdateResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelPersonUpdateResult}
   * @param {!PersonUpdateResult} fbeValue Default value, defaults is new PersonUpdateResult()
   * @returns {!PersonUpdateResult} PersonUpdateResult value
   */
  get (fbeValue = new PersonUpdateResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelPersonUpdateResult}
   * @param {!PersonUpdateResult} fbeValue PersonUpdateResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Person.fbeSize) <= fbeStructSize) {
      fbeValue.Person = this.Person.get()
    } else {
      fbeValue.Person = new Person()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Person.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelPersonUpdateResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelPersonUpdateResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelPersonUpdateResult}
   * @param {!PersonUpdateResult} fbeValue PersonUpdateResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelPersonUpdateResult}
   * @param {PersonUpdateResult} fbeValue PersonUpdateResult value
   */
  setFields (fbeValue) {
    this.Person.set(fbeValue.Person)
  }
}

export { FieldModelPersonUpdateResult };

/**
 * Fast Binary Encoding PersonUpdateResult model
 */
class PersonUpdateResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelPersonUpdateResult(this.buffer, 4)
  }

  /**
   * Get the PersonUpdateResult model
   * @this {!PersonUpdateResultModel}
   * @returns {!FieldModelPersonUpdateResult} model PersonUpdateResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!PersonUpdateResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!PersonUpdateResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return PersonUpdateResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!PersonUpdateResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelPersonUpdateResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!PersonUpdateResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!PersonUpdateResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!PersonUpdateResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!PersonUpdateResultModel}
   * @param {!PersonUpdateResult} value PersonUpdateResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!PersonUpdateResultModel}
   * @param {!PersonUpdateResult} value PersonUpdateResult value, defaults is new PersonUpdateResult()
   * @return {!object} Deserialized PersonUpdateResult value and its size
   */
  deserialize (value = new PersonUpdateResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new PersonUpdateResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new PersonUpdateResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!PersonUpdateResultModel}
   * @param {!number} prev Previous PersonUpdateResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { PersonUpdateResultModel };
